import React, { useEffect, useState } from "react";
import {
  Accordion,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Stacks from "@mui/material/Stack";
import { vasthuUrl } from "../component/Api";
import { useTranslation } from "react-i18next";

const CreateSlot = () => {
  const { t } = useTranslation();
  const userId = sessionStorage.getItem("userId");
  const token = sessionStorage.getItem("token");

  const today = new Date();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [meetingDuration, setMeetingDuration] = useState(30);
  const [maxDateLimit, setMaxDateLimit] = useState(null);
  const [yourScheduledMeetings, setYourScheduledMeetings] = useState([]);
  const [keyValue, setKeyValue] = useState(false);
  const [consultationModes, setConsultationModes] = useState([]);
  const [selectedConsultationMode, setSelectedConsultationMode] = useState(
    "Audio"
  );

  const [openSnack, setOpenSnack] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("error");
  const [saveData, setSaveData] = useState("error");
  const [consultantSlotUpdateModel, setConsultantSlotUpdateModel] = useState(
    false
  );

  const handleShow = (d) => {
    setConsultantSlotUpdateModel(true);
    setSaveData(d);
  };
  console.log("saveData", saveData);

  const handleCloseSnack = () => setOpenSnack(false);
  const handleClose = () => setConsultantSlotUpdateModel(false);

  useEffect(() => {
    const maxAllowedWeek = 8;
    const daysCountinWeek = 7;
    const allowedDate = new Date();
    allowedDate.setDate(
      allowedDate.getDate() + maxAllowedWeek * daysCountinWeek
    );
    setMaxDateLimit(allowedDate);

    fetchConsultationModes();
    getUpcomingServiceSchedules();
  }, [keyValue]);

  const fetchConsultationModes = async () => {
    try {
      const res = await vasthuUrl.get(
        `vasthu/getvasthuservicesbyconsultant/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = res.data;
      if (data.length > 0) {
        const item = data[0];
        const modes = [];
        if (item.audio) modes.push({ id: 1, mode: "Audio" });
        if (item.video) modes.push({ id: 2, mode: "Video" });
        setConsultationModes(modes);
      }
    } catch (e) {
      console.log("Error fetching consultation modes", e);
    }
  };

  const getHoursMinutesSec = (duration) => {
    const hrs = String(Math.floor(duration / 3600)).padStart(2, "0");
    const mins = String(Math.floor((duration % 3600) / 60)).padStart(2, "0");
    return `${hrs}:${mins}:00`;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-CA"); // yyyy-MM-dd
  };

  const getRoundedTimeSlots = (baseTimeInSeconds = null) => {
    const now = new Date();
    let minutes = now.getMinutes();
    const roundedMinutes = minutes <= 30 ? 30 : 60;
    now.setMinutes(roundedMinutes, 0, 0);

    const start = baseTimeInSeconds
      ? new Date(today.setHours(0, 0, 0, 0) + baseTimeInSeconds * 1000)
      : now;

    const slots = [];
    for (let i = 0; i < 48; i++) {
      const hrs = start.getHours();
      const mins = start.getMinutes();
      const label = `${hrs
        .toString()
        .padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
      const seconds = hrs * 3600 + mins * 60;
      slots.push({ label, seconds });
      start.setMinutes(start.getMinutes() + 30);
    }
    return slots;
  };

  const createBooking = async () => {
    const startFormatted = getHoursMinutesSec(startTime);
    const endFormatted = getHoursMinutesSec(endTime);
    const timeDifference = (endTime - startTime) / 60;

    if (!startDate || !endDate || !startTime || !endTime) {
      setMessage("Please check all inputs");
      setMessageType("error");
      setOpenSnack(true);
      return;
    }

    if (startDate > endDate) {
      setMessage("Scheduled From Date and To Date is invalid");
      setMessageType("error");
      setOpenSnack(true);
      return;
    }

    if (startTime >= endTime) {
      setMessage("Start time should be before end time");
      setMessageType("error");
      setOpenSnack(true);
      return;
    }

    if (
      timeDifference < meetingDuration ||
      timeDifference % meetingDuration !== 0
    ) {
      setMessage("Please choose a valid duration that fits evenly");
      setMessageType("error");
      setOpenSnack(true);
      return;
    }

    const payload = {
      consultantUuid: userId,
      consultationMode: selectedConsultationMode,
      scheduleFromDate: formatDate(startDate),
      scheduleToDate: formatDate(endDate),
      scheduleStartTime: startFormatted,
      scheduleEndTime: endFormatted,
      scheduleDuration: parseInt(meetingDuration),
      serviceStartDate: formatDate(startDate),
      serviceEndDate: formatDate(endDate),
      serviceStartTime: startFormatted,
      serviceEndTime: endFormatted,
    };

    try {
      const res = await vasthuUrl.post("saveserviceschedule", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data === "ALREADY SCHEDULED EXISTS FOR THIS GIVEN TIME ") {
        setMessage(res.data);
        setMessageType("error");
      } else {
        setMessage(res.data);
        setMessageType("success");
        resetForm();
        setKeyValue((prev) => !prev);
      }
      setOpenSnack(true);
    } catch (error) {
      console.error("Booking error", error);
      setMessage("Server error while creating booking");
      setMessageType("error");
      setOpenSnack(true);
    }
  };

  const resetForm = () => {
    setStartDate(null);
    setEndDate(null);
    setStartTime(null);
    setEndTime(null);
  };

  const getUpcomingServiceSchedules = async () => {
    try {
      const res = await vasthuUrl.get(`getupcomingserviceschedule/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setYourScheduledMeetings(res.data);
    } catch (error) {
      console.log("Error fetching schedules", error);
    }
  };

  return (
    <Container fluid>
      <Row className="justify-content-center">
        <Col lg={6}>
          <Card className="m-2 border border-warning rounded text-center">
            <Card.Body>
              <Row className="mb-2">
                <Col>
                  <h6>Start Date</h6>
                  <DatePicker
                    className="form_feild"
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Start Date"
                    minDate={today}
                    maxDate={maxDateLimit}
                  />
                </Col>

                <Col>
                  <h6>End Date</h6>
                  <DatePicker
                    className="form_feild"
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="End Date"
                    minDate={today}
                    maxDate={maxDateLimit}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <h6>{t("CON.APMT.NA.ST")}</h6>
                  <Form.Select
                    className="form_feild"
                    value={startTime || ""}
                    onChange={(e) => {
                      const selected = parseInt(e.target.value);
                      setStartTime(selected);
                      setEndTime(selected + 1800); // 30 mins after
                    }}
                  >
                    <option value="">Select Start Time</option>
                    {getRoundedTimeSlots().map((slot) => (
                      <option key={slot.seconds} value={slot.seconds}>
                        {slot.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col>
                  <h6>{t("CON.APMT.NA.ET")}</h6>
                  <Form.Select
                    className="form_feild"
                    value={endTime || ""}
                    onChange={(e) => setEndTime(parseInt(e.target.value))}
                    disabled={!startTime}
                  >
                    <option value="">Select End Time</option>
                    {startTime &&
                      getRoundedTimeSlots(startTime + 1800).map((slot) => (
                        <option key={slot.seconds} value={slot.seconds}>
                          {slot.label}
                        </option>
                      ))}
                  </Form.Select>
                </Col>
              </Row>
              <Row className="mb-2">
                <Form.Select
                  className="form_feild"
                  value={selectedConsultationMode}
                  onChange={(e) => setSelectedConsultationMode(e.target.value)}
                >
                  {consultationModes.map((mode) => (
                    <option key={mode.id} value={mode.mode}>
                      {mode.mode}
                    </option>
                  ))}
                </Form.Select>
              </Row>
              <Button variant="outline-warning" onClick={createBooking}>
                Create Slot
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Accordion>
        <Accordion.Item className="accordian_style" eventKey="0">
          <Accordion.Header className="all_background">
            Scheduled Slots
          </Accordion.Header>
          <Accordion.Body>
            <>
              {/* Desktop View */}
              <div className="d-none d-md-block">
                <Table striped bordered hover>
                  <thead className="border-warning">
                    <tr>
                      <th>From</th>
                      <th>To</th>
                      <th>Duration</th>
                      <th>Start Time</th>
                      <th>End Time</th>
                      <th>Mode</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody className="border-warning">
                    {yourScheduledMeetings.map((d, i) => (
                      <tr key={i}>
                        <td>{d.schedule_from_date}</td>
                        <td>{d.schedule_to_date}</td>
                        <td>{d.schedule_duration_in_minutes}</td>
                        <td>{d.schedule_start_time}</td>
                        <td>{d.schedule_end_time}</td>
                        <td>{d.consultation_mode}</td>
                        <td>
                          <Button
                            variant="outline-warning"
                            onClick={createBooking}
                          >
                            Update
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Mobile View */}
              <div className="d-block d-md-none">
                {yourScheduledMeetings.map((d, i) => (
                  <Card key={i} className="mb-3 border-warning shadow-sm">
                    <Card.Body>
                      <Card.Text className="mb-1">
                        <strong>From:</strong> {d.schedule_from_date}
                      </Card.Text>
                      <Card.Text className="mb-1">
                        <strong>To:</strong> {d.schedule_to_date}
                      </Card.Text>
                      <Card.Text className="mb-1">
                        <strong>Duration:</strong>{" "}
                        {d.schedule_duration_in_minutes} mins
                      </Card.Text>
                      <Card.Text className="mb-1">
                        <strong>Start Time:</strong> {d.schedule_start_time}
                      </Card.Text>
                      <Card.Text className="mb-1">
                        <strong>End Time:</strong> {d.schedule_end_time}
                      </Card.Text>
                      <Card.Text className="mb-1">
                        <strong>Mode:</strong> {d.consultation_mode}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Modal show={consultantSlotUpdateModel} onHide={handleClose}>
        <Modal.Header className="all_background" closeButton>
          <Modal.Title>Update Slot</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card className="m-2 border border-warning rounded text-center"></Card>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-around">
          <Button className="all_btn">Close</Button>
          <Button className="all_btn">Update</Button>
        </Modal.Footer>
      </Modal>

      <Stacks spacing={2} sx={{ width: "100%" }}>
        <Snackbar
          open={openSnack}
          autoHideDuration={3000}
          onClose={handleCloseSnack}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MuiAlert
            onClose={handleCloseSnack}
            severity={messageType}
            variant="filled"
          >
            {message}
          </MuiAlert>
        </Snackbar>
      </Stacks>
    </Container>
  );
};

export default CreateSlot;
